import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";

const CODE = ["↑", "↑", "↓", "↓", "←", "→", "←", "→", "B", "A"];
const LINES = [
  "LEGACY INPUT BUS [KONAMI-86] ........ HANDSHAKE",
  "OPERATOR CREDENTIALS ................ VERIFIED",
  "ARCHIVE 1986 ........................ UNSEALED",
];
const COLORS = ["#38BDF8", "#0284C7", "#FBBF24", "#F472B6", "#34D399", "#E2E8F0"];

const Lives = () => {
  const [n, setN] = useState(0);
  useEffect(() => {
    let iv;
    const t = setTimeout(() => {
      iv = setInterval(() => setN((v) => (v >= 30 ? 30 : v + 1)), 38);
    }, 2500);
    return () => {
      clearTimeout(t);
      clearInterval(iv);
    };
  }, []);
  return (
    <span className="f-orbitron text-xl font-black text-[#FBBF24]" data-testid="konami-lives">
      LIVES ×{n}
    </span>
  );
};

export default function KonamiFX({ onDone }) {
  const [off, setOff] = useState(false);
  const doneRef = useRef(false);

  const particles = useMemo(
    () =>
      Array.from({ length: 80 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: 2.1 + Math.random() * 2.6,
        dur: 2 + Math.random() * 1.6,
        size: 5 + Math.random() * 7,
        color: COLORS[i % COLORS.length],
        drift: (Math.random() - 0.5) * 120,
        rot: (Math.random() - 0.5) * 720,
      })),
    []
  );

  useEffect(() => {
    if (off) {
      const t = setTimeout(() => {
        if (!doneRef.current) {
          doneRef.current = true;
          onDone();
        }
      }, 700);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setOff(true), 6400);
    const skip = () => setOff(true);
    const kSkip = (e) => {
      if (e.key === "Escape" || e.key === "Enter" || e.key === " ") setOff(true);
    };
    window.addEventListener("keydown", kSkip);
    const arm = setTimeout(() => window.addEventListener("pointerdown", skip), 400);
    return () => {
      clearTimeout(t);
      clearTimeout(arm);
      window.removeEventListener("pointerdown", skip);
      window.removeEventListener("keydown", kSkip);
    };
  }, [off, onDone]);

  return (
    <motion.div
      className="fixed inset-0 z-[90] cursor-pointer overflow-hidden bg-[#020617]"
      data-testid="konami-fx"
      initial={{ opacity: 0 }}
      animate={
        off
          ? { opacity: 1, scaleY: [1, 0.004, 0.004], scaleX: [1, 1, 0], transition: { duration: 0.6, times: [0, 0.55, 1], ease: "easeIn" } }
          : { opacity: 1, transition: { duration: 0.25 } }
      }
      exit={{ opacity: 0 }}
      style={{ transformOrigin: "50% 50%" }}
    >
      {/* center glow + vignette + scanlines */}
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 45%, rgba(56,189,248,0.16), transparent 60%)" }} />
      <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.55) 100%)" }} />
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{ background: "repeating-linear-gradient(0deg, rgba(255,255,255,0.05) 0px, rgba(255,255,255,0.05) 1px, transparent 1px, transparent 3px)" }}
      />

      {/* boot flash + glitch bars */}
      <motion.div className="absolute inset-0 bg-[#38BDF8]" initial={{ opacity: 0.7 }} animate={{ opacity: 0 }} transition={{ duration: 0.5 }} />
      {[14, 38, 63, 82].map((top, i) => (
        <motion.div
          key={top}
          className="absolute left-0 right-0 bg-[#38BDF8]/60"
          style={{ top: `${top}%`, height: 3 + i * 2 }}
          initial={{ opacity: 0, x: i % 2 ? -80 : 80 }}
          animate={{ opacity: [0, 1, 0, 0.7, 0], x: 0 }}
          transition={{ duration: 0.55, delay: 0.05 * i }}
        />
      ))}

      {/* slam flash */}
      <motion.div className="absolute inset-0 bg-white" initial={{ opacity: 0 }} animate={{ opacity: [0, 0.65, 0] }} transition={{ duration: 0.4, delay: 2.0 }} />

      {/* pixel confetti */}
      {particles.map((p) => (
        <motion.span
          key={p.id}
          className="absolute top-0"
          style={{ left: `${p.left}%`, width: p.size, height: p.size, background: p.color }}
          initial={{ y: "-8vh", opacity: 0, rotate: 0 }}
          animate={{ y: "108vh", x: p.drift, opacity: [0, 1, 1, 0.8], rotate: p.rot }}
          transition={{ duration: p.dur, delay: p.delay, ease: "linear" }}
        />
      ))}

      <div className="relative flex h-full flex-col items-center justify-center gap-7 px-6">
        {/* terminal lines */}
        <div className="flex flex-col gap-1.5" data-testid="konami-terminal">
          {LINES.map((l, i) => (
            <motion.span
              key={l}
              className="f-mono text-[9px] tracking-[0.25em] text-[#38BDF8]/80"
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.35 + i * 0.35 }}
            >
              ▸ {l}
            </motion.span>
          ))}
        </div>

        {/* code keys lighting up */}
        <div className="flex flex-wrap items-center justify-center gap-2" data-testid="konami-code-keys">
          {CODE.map((k, i) => (
            <motion.span
              key={`${k}-${i}`}
              className="f-orbitron flex h-9 w-9 items-center justify-center rounded-md border text-sm font-black"
              initial={{ borderColor: "rgba(148,163,184,0.25)", color: "#334155", scale: 1 }}
              animate={{
                borderColor: "rgba(56,189,248,0.9)",
                color: "#7DD3FC",
                scale: [1, 1.35, 1],
                boxShadow: "0 0 18px rgba(56,189,248,0.55)",
              }}
              transition={{ duration: 0.28, delay: 0.6 + i * 0.12 }}
            >
              {k}
            </motion.span>
          ))}
        </div>

        {/* slam title */}
        <motion.div
          className="flex flex-col items-center gap-3 text-center"
          initial={{ scale: 3.2, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.22, delay: 2.0, ease: "easeOut" }}
        >
          <h2
            className="f-orbitron text-4xl font-black tracking-wide text-white sm:text-6xl"
            style={{ textShadow: "3px 0 0 rgba(56,189,248,0.8), -3px 0 0 rgba(244,114,182,0.7), 0 0 40px rgba(56,189,248,0.5)" }}
          >
            CHEAT CODE ACCEPTED
          </h2>
          <motion.div
            className="flex items-center gap-6"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 2.5 }}
          >
            <span className="f-mono text-[10px] tracking-[0.4em] text-[#94A3B8]">∞ IDEAS UNLOCKED</span>
            <Lives />
          </motion.div>
        </motion.div>

        {/* rank card */}
        <motion.div
          className="flex items-center gap-3 rounded-xl border border-[#38BDF8]/40 bg-white/5 px-6 py-3 backdrop-blur-md"
          data-testid="konami-rank-card"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 3.6 }}
        >
          <span className="f-orbitron text-lg text-[#FBBF24]">★</span>
          <span className="f-mono text-[10px] tracking-[0.3em] text-[#E2E8F0]">
            RANK UNLOCKED — KONAMI OPERATOR · CLASS OF 1986
          </span>
        </motion.div>

        <motion.span
          className="f-mono absolute bottom-6 text-[8px] tracking-[0.35em] text-[#475569]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.7 }}
          transition={{ delay: 1.6 }}
        >
          CLICK / ESC TO SKIP
        </motion.span>
      </div>
    </motion.div>
  );
}
